import { PaymentPurpose } from '../enums';
import { formatAccountDisplay, getRecipientEmail } from './recipientHelpers';
import { parseAmountInputValue } from './userPreferencesHelpers';

const NAME_STOP_WORDS = new Set([
  'sa',
  'de',
  'cv',
  'the',
  'and',
  'for',
  'ltd',
  'llc',
  'gmbh',
  'inc',
  'corp',
  'plc',
  'bv',
  'ag',
  'co',
  'br',
  'mx',
  'us',
  'uk',
  'de',
  'spa',
  'srl',
]);

const EXPLICIT_NAME_KEYS = [
  'beneficiaryName',
  'beneficiary_name',
  'recipientName',
  'recipient_name',
  'payeeName',
  'payee_name',
  'companyName',
  'company_name',
  'sellerName',
  'seller_name',
  'seller',
  'beneficiary',
  'payee',
  'beneficiaryFullName',
  'creditorName',
  'merchantName',
  'name',
];

function normalizeIban(value) {
  return String(value || '')
    .replace(/\s/g, '')
    .toUpperCase();
}

export function normalizeNameForMatch(value) {
  return String(value || '')
    .replace(/[[\]]/g, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s@.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getSignificantTokens(normalizedName) {
  return normalizedName
    .split(' ')
    .filter((token) => token.length >= 3 && !NAME_STOP_WORDS.has(token));
}

export function nameSimilarity(nameA, nameB) {
  const normA = normalizeNameForMatch(nameA);
  const normB = normalizeNameForMatch(nameB);
  if (!normA || !normB) return 0;
  if (normA === normB) return 1;
  if (normA.includes(normB) || normB.includes(normA)) return 0.92;

  const tokensA = getSignificantTokens(normA);
  const tokensB = getSignificantTokens(normB);
  if (!tokensA.length || !tokensB.length) return 0;

  const matched = tokensA.filter((token) => tokensB.some((other) => other.includes(token) || token.includes(other)));
  return matched.length / Math.min(tokensA.length, tokensB.length);
}

function normalizeIdentifier(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '');
}

function collectStringsFromValue(value, strings = []) {
  if (value == null) return strings;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) strings.push(trimmed);
    return strings;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectStringsFromValue(item, strings));
    return strings;
  }
  if (typeof value === 'object') {
    Object.values(value).forEach((item) => collectStringsFromValue(item, strings));
  }
  return strings;
}

function extractExplicitNames(data) {
  const names = [];
  EXPLICIT_NAME_KEYS.forEach((key) => {
    if (data[key]) names.push(String(data[key]));
  });
  return names;
}

function extractEmailsFromStrings(strings) {
  const emails = new Set();
  strings.forEach((text) => {
    const matches = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi);
    if (matches) {
      matches.forEach((email) => emails.add(email.toLowerCase()));
    }
  });
  return emails;
}

function recipientIdentifiers(recipient) {
  const values = [
    recipient.name,
    recipient.displayName,
    getRecipientEmail(recipient),
    recipient.email,
    recipient.emailAddress,
    recipient.contactEmail,
    recipient.iban,
    recipient.accountNumber,
    recipient.accountId,
    recipient.bankAccountNumber,
    recipient.pixKey,
    formatAccountDisplay(recipient),
  ];
  return values.filter((value) => value && value !== '—').map((value) => String(value));
}

function scoreRecipientAgainstDocument(recipient, { explicitNames, corpusNormalized, emails }) {
  const recipientName = recipient.name || recipient.displayName || '';
  let score = 0;

  explicitNames.forEach((name) => {
    score = Math.max(score, nameSimilarity(name, recipientName));
  });

  const recipientNorm = normalizeNameForMatch(recipientName);
  const tokens = getSignificantTokens(recipientNorm);
  if (tokens.length && corpusNormalized) {
    const tokenHits = tokens.filter((token) => corpusNormalized.includes(token)).length;
    score = Math.max(score, tokenHits / tokens.length);
  }

  const normalizedIds = recipientIdentifiers(recipient).map(normalizeIdentifier);
  emails.forEach((email) => {
    const normalizedEmail = normalizeIdentifier(email);
    if (normalizedIds.some((id) => id === normalizedEmail || id.includes(normalizedEmail) || normalizedEmail.includes(id))) {
      score = Math.max(score, 0.95);
    }
  });

  return score;
}

export function normalizePaymentPurpose(value) {
  if (!value) return null;
  const raw = String(value).trim();
  const upper = raw.toUpperCase().replace(/-/g, '_').replace(/\s+/g, '_');
  if (Object.values(PaymentPurpose).includes(upper)) return upper;

  const snake = raw.toLowerCase().replace(/[\s-]+/g, '_');
  const fromSnake = Object.values(PaymentPurpose).find((purpose) => purpose.toLowerCase() === snake);
  if (fromSnake) return fromSnake;

  const compact = snake.replace(/_/g, '');
  const fromCompact = Object.values(PaymentPurpose).find((purpose) => purpose.toLowerCase().replace(/_/g, '') === compact);
  return fromCompact || null;
}

export function getParsedInvoiceCurrency(data) {
  if (!data?.currency) return null;
  const code = String(data.currency).trim().toUpperCase();
  return code || null;
}

function filterRecipientsByInvoiceCurrency(recipients, parsedCurrency, defaultSendCurrency = 'EUR') {
  if (!parsedCurrency || parsedCurrency === defaultSendCurrency) {
    return recipients;
  }

  return recipients.filter((recipient) => {
    const recipientCurrency = recipient?.currency ? String(recipient.currency).trim().toUpperCase() : null;
    return recipientCurrency === parsedCurrency;
  });
}

export function matchRecipientFromExtractedData(data, recipients, { defaultSendCurrency = 'EUR' } = {}) {
  if (!data || !Array.isArray(recipients) || recipients.length === 0) return null;

  const parsedCurrency = getParsedInvoiceCurrency(data);
  const candidates = filterRecipientsByInvoiceCurrency(recipients, parsedCurrency, defaultSendCurrency);
  if (parsedCurrency && parsedCurrency !== defaultSendCurrency && candidates.length === 0) {
    return null;
  }

  const recipientsToMatch = candidates.length > 0 ? candidates : recipients;

  const extractedIban = normalizeIban(data.iban || data.IBAN || data.accountNumber || data.accountIban);
  if (extractedIban) {
    const byIban = recipientsToMatch.find((recipient) => {
      const display = normalizeIban(formatAccountDisplay(recipient));
      const bankAccount = normalizeIban(recipient.bankAccountNumber || recipient.iban);
      return (display && display.includes(extractedIban)) || (bankAccount && bankAccount === extractedIban);
    });
    if (byIban) return byIban.id;
  }

  const pixKey = data.pixKey || data.pix_key;
  if (pixKey) {
    const normalizedPix = normalizeIdentifier(pixKey);
    const byPix = recipientsToMatch.find((recipient) =>
      recipientIdentifiers(recipient).some((id) => {
        const normalized = normalizeIdentifier(id);
        return normalized === normalizedPix || normalized.includes(normalizedPix) || normalizedPix.includes(normalized);
      }),
    );
    if (byPix) return byPix.id;
  }

  const documentStrings = collectStringsFromValue(data);
  const explicitNames = extractExplicitNames(data);
  const emails = extractEmailsFromStrings([
    ...documentStrings,
    data.beneficiaryEmail,
    data.beneficiary_email,
    data.email,
    data.pixKey,
    data.pix_key,
  ].filter(Boolean));
  const corpusNormalized = normalizeNameForMatch(documentStrings.join(' '));

  let bestMatch = null;
  let bestScore = 0;

  recipientsToMatch.forEach((recipient) => {
    const score = scoreRecipientAgainstDocument(recipient, { explicitNames, corpusNormalized, emails });
    if (score > bestScore) {
      bestScore = score;
      bestMatch = recipient;
    }
  });

  if (bestMatch && bestScore >= 0.5) return bestMatch.id;

  return null;
}

export function getInvoiceRecipientMatchError(recipients, { parsedCurrency, defaultSendCurrency = 'EUR' } = {}) {
  if (!Array.isArray(recipients) || recipients.length === 0) {
    return 'You do not have any recipients yet. Add a recipient first, then upload your invoice again.';
  }

  if (parsedCurrency && parsedCurrency !== defaultSendCurrency) {
    const hasCurrencyRecipient = recipients.some(
      (recipient) => String(recipient?.currency || '').trim().toUpperCase() === parsedCurrency,
    );
    if (!hasCurrencyRecipient) {
      return `We could not find a recipient for ${parsedCurrency}. Add a ${parsedCurrency} recipient, then upload your invoice again.`;
    }

    return `We could not match this invoice to a ${parsedCurrency} recipient. Select the correct recipient below or add a new one — we will apply the invoice details when you continue.`;
  }

  return 'We could not match this invoice to any of your recipients. Select the correct recipient below or add a new one — we will apply the invoice details when you continue.';
}

export function buildInvoiceFormState(data, matchedRecipient, { formatNumber, defaultSendCurrency = 'EUR', spotRate = null }) {
  const parsedCurrency = getParsedInvoiceCurrency(data);
  const recipientCurrency = matchedRecipient?.currency ? String(matchedRecipient.currency).trim().toUpperCase() : null;
  const receiveCurrency = recipientCurrency || parsedCurrency || defaultSendCurrency;
  const sendCurrency = defaultSendCurrency;
  const currencyMismatch = Boolean(
    parsedCurrency && parsedCurrency !== defaultSendCurrency && recipientCurrency && parsedCurrency !== recipientCurrency,
  );

  const docType = data.documentType ?? data.swift_document_type ?? 'invoice';
  const docId = data.documentId ?? data.swift_invoice_id ?? '';
  const paymentReference =
    (typeof data.paymentReference === 'string' && data.paymentReference.trim()) || (docId && String(docId).trim()) || '';
  const paymentPurpose = normalizePaymentPurpose(data.paymentPurpose) || PaymentPurpose.PurchaseOfGoods;

  let sendAmount = '';
  let receiveAmount = '';
  let lastEditedField = 'receive';

  const amountNum = data.amount != null && !Number.isNaN(Number(data.amount)) ? Number(data.amount) : null;

  if (amountNum != null && !currencyMismatch) {
    if (parsedCurrency === sendCurrency) {
      sendAmount = formatNumber(amountNum);
      lastEditedField = 'send';
      if (sendCurrency !== receiveCurrency && spotRate) {
        receiveAmount = formatNumber(amountNum * spotRate);
      }
    } else if (!parsedCurrency || parsedCurrency === receiveCurrency) {
      receiveAmount = formatNumber(amountNum);
      lastEditedField = 'receive';
      if (sendCurrency !== receiveCurrency && spotRate) {
        sendAmount = formatNumber(amountNum / spotRate);
      }
    }
  }

  return {
    sendCurrency,
    receiveCurrency,
    sendAmount,
    receiveAmount,
    lastEditedField,
    paymentReference,
    paymentPurpose,
    transactionDocumentType: docType,
    transactionDocumentId: docId || paymentReference,
    currencyMismatch,
    parsedCurrency,
  };
}

export function applyInvoiceExtractedDataToForm(data, recipient, { formatNumber, defaultSendCurrency = 'EUR', spotRate = null }) {
  if (!data || !recipient) return null;

  const formState = buildInvoiceFormState(data, recipient, { formatNumber, defaultSendCurrency, spotRate });
  return {
    ...formState,
    hasAmount: Boolean(formState.sendAmount || formState.receiveAmount),
    currencyMismatch: formState.currencyMismatch,
    parsedCurrency: formState.parsedCurrency,
  };
}

export function getCrossBorderQuoteAmounts(quote) {
  if (!quote) {
    return {
      id: null,
      amountSend: null,
      amountReceive: null,
      totalFee: null,
      spotRate: null,
      expiry: null,
    };
  }

  return {
    id: quote.id ?? quote.Id ?? null,
    amountSend: quote.amountSend ?? quote.AmountSend ?? null,
    amountReceive: quote.amountReceive ?? quote.AmountReceive ?? null,
    totalFee: quote.totalFee ?? quote.TotalFee ?? null,
    spotRate: quote.spotRate ?? quote.SpotRate ?? null,
    expiry: quote.expiry ?? quote.Expiry ?? null,
  };
}

export function parseFormattedAmount(value, preferencesOrLayout) {
  if (arguments.length < 2 || preferencesOrLayout == null) {
    return parseFloat(String(value || '').replace(/,/g, '')) || 0;
  }
  return parseAmountInputValue(value, preferencesOrLayout);
}

export function canRequestQuoteFromParsedFields({
  selectedRecipient,
  sendAmount,
  receiveAmount,
  paymentReference,
  paymentPurpose,
  sendCurrency,
  receiveCurrency,
  isSwiftTransfer,
  transactionDocumentType,
  transactionDocumentId,
  transactionDocumentFile,
}) {
  if (!selectedRecipient) return false;
  if (!sendAmount && !receiveAmount) return false;
  if (!paymentReference?.trim()) return false;
  if (!paymentPurpose?.trim()) return false;
  if (sendCurrency === receiveCurrency) return false;
  if (isSwiftTransfer && (!transactionDocumentType || !transactionDocumentId || !transactionDocumentFile)) return false;
  return true;
}
